/**
 * <PageHeader> — 13-ui-screens.md §3.7, §4.5.
 *
 * antd removed `PageHeader` in v5. Every screen header band carries the
 * signature 56 × 4 `--brand-accent` rule under the title; it is the single
 * strongest brand signal in the system (14-theme.md §5.3) — do not omit it,
 * do not recolour it, do not round it.
 */
import type { ReactNode } from 'react';

export interface PageHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Rendered above the title — breadcrumb / back link / context line. */
  breadcrumb?: ReactNode;
  /** Pushed to the right of the title row. */
  actions?: ReactNode;
  /** Extra rows under the subtitle (the control strip on Daily Job Entry). */
  children?: ReactNode;
  /** Daily Job Entry and Payroll Run use `16px 20px 0` — they carry extra rows. */
  flush?: boolean;
}

export function PageHeader({
  title,
  subtitle,
  breadcrumb,
  actions,
  children,
  flush = false,
}: PageHeaderProps) {
  return (
    <div
      style={{
        background: 'var(--surface-card)',
        borderBottom: '1px solid var(--border-subtle)',
        padding: flush ? '16px 20px 0' : '16px 20px 14px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
        <div style={{ minWidth: 0 }}>
          {breadcrumb ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                marginBottom: 6,
                fontSize: 12,
              }}
            >
              {breadcrumb}
            </div>
          ) : null}
          <h1 className="cm-page-title">{title}</h1>
          <span className="cm-rule" />
          {subtitle ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{subtitle}</div>
          ) : null}
        </div>
        {actions ? (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            {actions}
          </div>
        ) : null}
      </div>
      {children}
    </div>
  );
}

/** The body band under every header. Daily Job Entry passes `clearsFooter`. */
export function PageBody({
  children,
  clearsFooter = false,
}: {
  children: ReactNode;
  clearsFooter?: boolean;
}) {
  return (
    <div style={{ padding: clearsFooter ? '16px 20px 200px' : '16px 20px 40px' }}>{children}</div>
  );
}

/** `JOBS BOOKED` — the card / panel section label. */
export function SectionLabel({ children }: { children: ReactNode }) {
  return <span className="cm-section-label">{children}</span>;
}

/** `JOB PAPER REF` — the 10 px Montserrat uppercase form label. Form tokens
 *  cannot produce it (14-theme.md §9); pass this into `Form.Item label`. */
export function FieldLabel({ children, faint }: { children: ReactNode; faint?: boolean }) {
  return <span className={faint ? 'cm-label cm-label--faint' : 'cm-label'}>{children}</span>;
}
